import { AppCore } from "../../api/core/AppCore";
import { EventObject } from "../../util/event/EventObject";
import { Logger } from "../../util/log/Logger";
import { CommonGameEvent } from "../controller/event/CommonGameEvent";
import { ErrorData } from "./data/ErrorData";
import { FreeRoundsData } from "./data/FreeRoundsData";
import { ResponsibleGamingData } from "./data/ResponsibleGamingData";
/**
 * AbstractModel.ts
 */
export class AbstractModel {
    constructor() {
        this.initialised = false;
        this.requestPermitted = true;
        this.demoPlay = false;
        this.errorData = new ErrorData();
        this.eventBus = AppCore.getInstance().getEventBus();
        this.responsibleGaming = new ResponsibleGamingData(this);
        this.freeRoundsData = new FreeRoundsData();
        this.analyticsEnabled = true;
    }
    getHasBonusGame() {
        return this.hasBonusGame;
    }
    setHasBonusGame(value) {
        this.hasBonusGame = value;
    }
    /**
     * Register an event listener with the `scope` event bus.
     * Only one function can be bound to an event per `scope`.
     *
     * Call `unregister(type)` to remove the listener.
     */
    register(event, handler, scope) {
        this.eventBus.addEventListener(event.getType(), handler, scope);
    }
    /**
     * Unregister an event listener from the `scope` event bus.
     */
    unregister(event, scope) {
        this.eventBus.removeEventListener(event.getType(), scope);
    }
    unregisterAll(scope) {
        this.eventBus.removeEventListenersFor(scope);
    }
    /**
     * Dispatch an event to the `scope` event bus.
     * @param event - The event to dispatch.
     */
    dispatch(event) {
        Logger.verbose(`AbstractModel.dispatch: ${event.getType()}`);
        this.eventBus.dispatchEvent(event);
    }
    /**
     * Awaits all async event listeners for the given `event`.
     *
     * Most event listeners do not return promises so they resolve instantly.
     *
     * @param event - The event to dispatch.
     */
    async dispatchBlockingEvent(event) {
        Logger.verbose(`AbstractModel.dispatch blocking: ${event.getType()}`);
        await this.eventBus.dispatchBlockingEvent(event);
    }
    setRoomName(value) {
        this.roomName = value;
    }
    setInitialised(value) {
        this.initialised = value;
    }
    setStake(value) {
        this.stake = value;
    }
    setRequestPermitted(value) {
        this.requestPermitted = value;
    }
    setRealityCheckDetails(value) {
        this.realityCheckDetails = value;
    }
    setWinLoss(value) {
        this.winLoss = value;
    }
    setDemoPlay(value) {
        this.demoPlay = value;
    }
    setWrapperConfig(config) {
        this.operatorConfig = config.getOperatorConfig();
        this.operatorParameters = config.getOperatorParameters();
    }
    setBalance(value) {
        this.balance = value;
    }
    getBalance() {
        return this.balance;
    }
    getResponsibleGaming() {
        return this.responsibleGaming;
    }
    setLoaderProgress(value) {
        this.loaderProgress = value;
    }
    getRealityCheckDetails() {
        return this.realityCheckDetails;
    }
    getWinLoss() {
        return this.winLoss;
    }
    getRoomName() {
        return this.roomName;
    }
    getCurrentStake() {
        return this.stake.getCurrent();
    }
    getStake() {
        return this.stake;
    }
    isInitialised() {
        return this.initialised;
    }
    isRequestPermitted() {
        return this.requestPermitted;
    }
    isDemoPlay() {
        return this.demoPlay;
    }
    getErrorData() {
        return this.errorData;
    }
    getLoaderProgress() {
        return this.loaderProgress;
    }
    hasError() {
        return this.errorData.hasError();
    }
    getFreeRoundsData() {
        return this.freeRoundsData;
    }
    getOperatorConfig() {
        return this.operatorConfig;
    }
    getOperatorParameters() {
        return this.operatorParameters;
    }
    setFreeRoundsAvailable(value) {
        this.freeRoundsAvailable = value;
    }
    isFreeRoundsAvailable() {
        return this.freeRoundsAvailable;
    }
    setPurchaseSpinConfirmValue(value) {
        this.responsibleGaming.setPurchaseSpinConfirmValue(value || "");
    }
    setGameReplay(value) {
        this.gameReplay = value;
    }
    getGameReplay() {
        return this.gameReplay;
    }
    reset() {
        this.hasBonusGame = false;
    }
    setAnalyticsEnabled(value) {
        this.analyticsEnabled = value;
    }
    isAnalyticsEnabled() {
        return this.analyticsEnabled;
    }
    dispatchError(category, operation, value) {
        if (!this.analyticsEnabled) {
            return;
        }
        this.dispatch(new EventObject(CommonGameEvent.BI_ERROR.getType(), {
            category: category,
            operation: operation,
            value: value
        }));
    }
    dispatchKPI(category, operation, value) {
        if (!this.analyticsEnabled) {
            return;
        }
        this.dispatch(new EventObject(CommonGameEvent.BI_KPI.getType(), {
            category: category,
            operation: operation,
            value: value
        }));
    }
    dispatchDebug(category, operation, value) {
        if (!this.analyticsEnabled) {
            return;
        }
        this.dispatch(new EventObject(CommonGameEvent.BI_DEBUG.getType(), {
            category: category,
            operation: operation,
            value: value
        }));
    }
}
//# sourceMappingURL=AbstractModel.js.map